import React, { useRef, useMemo, Suspense } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Canvas, useFrame, useThree } from '@react-three/fiber';
import { Float, RoundedBox, MeshTransmissionMaterial } from '@react-three/drei';
import * as THREE from 'three';

/* ─── styled-components ─── */

const HeroContainer = styled.section`
  min-height: 100vh;
  min-height: 100dvh;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  position: relative;
  background: linear-gradient(135deg, #1a3a5c 0%, #1e4976 40%, #205d8a 70%, #2980b9 100%);
  overflow: hidden;
  box-sizing: border-box;

  .dark-mode & {
    background: #0e1420;
  }
`;

const DevicesCanvas = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1;
`;

const HeroContent = styled.div`
  position: relative;
  z-index: 2;
  width: 100%;
  max-width: 960px;
  margin: 0 auto;
  padding: 0 2rem;
  padding-top: 18vh;
  color: var(--white);
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  pointer-events: none;

  @media (max-width: 768px) {
    padding: 0 1.5rem;
    padding-top: 18vh;
  }

  @media (max-width: 480px) {
    padding: 0 1.2rem;
    padding-top: 20vh;
  }

  @media (max-height: 500px) {
    padding-top: 70px;
  }
`;

const HeroTitle = styled(motion.h1)`
  font-size: 3.6rem;
  font-weight: 800;
  margin-bottom: 1rem;
  line-height: 1.15;
  text-shadow: 0 2px 30px rgba(0, 0, 0, 0.3);

  .highlight {
    color: var(--secondary);
    position: relative;
    display: inline;
  }

  @media (max-width: 992px) {
    font-size: 3rem;
  }

  @media (max-width: 768px) {
    font-size: 2.3rem;
    margin-bottom: 0.8rem;
  }

  @media (max-width: 480px) {
    font-size: 1.8rem;
    margin-bottom: 0.5rem;
  }
  
  @media (max-width: 360px) {
    font-size: 1.5rem;
  }

  @media (max-height: 500px) {
    font-size: 1.2rem;
    margin-bottom: 0.1rem;
    line-height: 1.1;
  }
`;

const HeroSubtitle = styled(motion.p)`
  font-size: 1.2rem;
  color: #ffffff !important;
  margin-bottom: 1.5rem;
  line-height: 1.5;
  max-width: 600px;
  font-weight: 500;
  text-shadow: 0 2px 15px rgba(0, 0, 0, 0.4);

  @media (max-width: 768px) {
    font-size: 1rem;
    margin-bottom: 1.2rem;
  }

  @media (max-width: 480px) {
    font-size: 0.9rem;
    margin-bottom: 1rem;
  }

  @media (max-height: 500px) {
    font-size: 0.75rem;
    margin-bottom: 0.2rem;
    line-height: 1.2;
  }
`;



const ButtonContainer = styled(motion.div)`
  display: flex;
  gap: 1.5rem;
  justify-content: center;

  @media (max-width: 576px) {
    flex-direction: column;
    width: 100%;
    gap: 1rem;
  }

  @media (max-height: 500px) {
    flex-direction: row;
    gap: 1rem;
  }
`;

const PrimaryButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background-color: var(--secondary);
  color: var(--black);
  padding: 1rem 2.5rem;
  border-radius: var(--border-radius-md);
  font-weight: 600;
  transition: all var(--transition-fast);
  position: relative;
  overflow: hidden;
  border: none;
  cursor: pointer;
  font-size: 1rem;

  @media (max-width: 768px) {
    padding: 0.8rem 1.8rem;
    font-size: 0.9rem;
  }

  @media (max-width: 576px) {
    width: 100%;
    padding: 0.85rem 1.5rem;
  }
  
  @media (max-width: 480px) {
    padding: 0.75rem 1.2rem;
    font-size: 0.85rem;
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
    transform: translateX(-100%);
  }

  &:hover {
    background-color: var(--accent);
    color: var(--white);
    transform: translateY(-5px);
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.2);

    &::before {
      transform: translateX(100%);
      transition: transform 0.8s ease;
    }
  }
`;

const SecondaryButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background-color: transparent;
  color: var(--white);
  padding: 1rem 2.5rem;
  border-radius: var(--border-radius-md);
  font-weight: 600;
  border: 2px solid var(--white);
  transition: all var(--transition-fast);
  position: relative;
  overflow: hidden;
  cursor: pointer;
  font-size: 1rem;

  @media (max-width: 768px) {
    padding: 0.8rem 1.8rem;
    font-size: 0.9rem;
  }

  @media (max-width: 576px) {
    width: 100%;
    padding: 0.85rem 1.5rem;
  }
  
  @media (max-width: 480px) {
    padding: 0.75rem 1.2rem;
    font-size: 0.85rem;
  }

  @media (max-height: 500px) {
    padding: 0.6rem 1.5rem;
    font-size: 0.8rem;
    width: auto;
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: var(--white);
    transform: scaleX(0);
    transform-origin: right;
    transition: transform var(--transition-fast);
    z-index: -1;
  }

  &:hover {
    color: var(--black);
    border-color: var(--white);
    transform: translateY(-5px);
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.2);

    &::before {
      transform: scaleX(1);
      transform-origin: left;
    }
  }
`;

const ScrollIndicator = styled(motion.div)`
  position: absolute;
  bottom: 2rem;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  color: var(--white);
  opacity: 0.7;
  z-index: 2;

  @media (max-width: 768px) {
    display: none;
  }

  @media (max-height: 500px) {
    display: none;
  }

  .mouse {
    width: 30px;
    height: 50px;
    border: 2px solid var(--white);
    border-radius: 20px;
    display: flex;
    justify-content: center;
    margin-bottom: 0.5rem;
  }

  .wheel {
    width: 4px;
    height: 8px;
    background-color: var(--white);
    border-radius: 2px;
    margin-top: 8px;
    animation: scroll 1.5s ease infinite;
  }

  span {
    font-size: 0.8rem;
    letter-spacing: 1px;
  }

  @keyframes scroll {
    0% { transform: translateY(0); opacity: 1; }
    100% { transform: translateY(20px); opacity: 0; }
  }
`;

/* ─── Three.js components ─── */

// Animated particles with glow
function Particles({ count = 50 }) {
  const mesh = useRef();
  const positions = useMemo(() => {
    const pos = new Float32Array(count * 3);
    for (let i = 0; i < count; i++) {
      pos[i * 3]     = (Math.random() - 0.5) * 16;
      pos[i * 3 + 1] = (Math.random() - 0.5) * 8;
      pos[i * 3 + 2] = (Math.random() - 0.5) * 8 - 2;
    }
    return pos;
  }, [count]);

  useFrame((state) => {
    if (mesh.current) {
      mesh.current.rotation.y = state.clock.elapsedTime * 0.03;
      mesh.current.rotation.x = Math.sin(state.clock.elapsedTime * 0.01) * 0.1;
    }
  });

  return (
    <points ref={mesh}>
      <bufferGeometry>
        <bufferAttribute attach="attributes-position" count={count} array={positions} itemSize={3} />
      </bufferGeometry>
      <pointsMaterial size={0.04} color="#FED136" transparent opacity={0.6} sizeAttenuation />
    </points>
  );
}

// Starry background — small white dots
function Stars({ count = 400 }) {
  const mesh = useRef();
  const { positions, sizes } = useMemo(() => {
    const pos = new Float32Array(count * 3);
    const sz = new Float32Array(count);
    for (let i = 0; i < count; i++) {
      pos[i * 3]     = (Math.random() - 0.5) * 140;
      pos[i * 3 + 1] = (Math.random() - 0.5) * 90;
      pos[i * 3 + 2] = (Math.random() - 0.5) * 50 - 15;
      sz[i] = Math.random() * 0.04 + 0.01;
    }
    return { positions: pos, sizes: sz };
  }, [count]);

  useFrame((state) => {
    if (mesh.current) {
      mesh.current.rotation.y = state.clock.elapsedTime * 0.008;
      mesh.current.rotation.x = Math.sin(state.clock.elapsedTime * 0.005) * 0.05;
    }
  });

  return (
    <points ref={mesh}>
      <bufferGeometry>
        <bufferAttribute attach="attributes-position" count={count} array={positions} itemSize={3} />
      </bufferGeometry>
      <pointsMaterial size={0.035} color="#ffffff" transparent opacity={0.6} sizeAttenuation />
    </points>
  );
}

// Glowing screen material helper — emissive screen effect
function ScreenContent({ width, height, position, children }) {
  return (
    <group position={position}>
      {children}
    </group>
  );
}

// Single animated ripple ring
function RippleRing({ radius, speed, delay, color, thickness }) {
  const ref = useRef();
  useFrame((state) => {
    if (!ref.current) return;
    const t = ((state.clock.elapsedTime * speed + delay) % 1);
    const scale = 0.3 + t * 0.7;
    ref.current.scale.set(scale, scale, 1);
    // fade in then fade out
    ref.current.material.opacity = t < 0.3
      ? (t / 0.3) * 0.12
      : 0.12 * (1 - (t - 0.3) / 0.7);
  });

  return (
    <mesh ref={ref} rotation={[-Math.PI / 2, 0, 0]}>
      <ringGeometry args={[radius - thickness, radius, 64]} />
      <meshBasicMaterial color={color} transparent opacity={0} side={THREE.DoubleSide} />
    </mesh>
  );
}

// Ripple effect under each device (multiple expanding rings)
function RippleEffect({ position, radiusX = 1.8, color = '#FED136', rings = 3 }) {
  return (
    <group position={position}>
      {Array.from({ length: rings }, (_, i) => (
        <RippleRing
          key={i}
          radius={radiusX}
          thickness={radiusX * 0.08}
          speed={0.4}
          delay={i / rings}
          color={color}
        />
      ))}
    </group>
  );
}

/* ── Desktop browser window ── */
function DesktopBrowser({ position = [0, 0, 0], scale = 1 }) {
  const group = useRef();
  const screenRef = useRef();

  useFrame((state) => {
    if (group.current) {
      const t = state.clock.elapsedTime;
      group.current.rotation.y = Math.sin(t * 0.5) * 0.12 + 0.15;
      group.current.rotation.x = Math.cos(t * 0.3) * 0.04 - 0.05;
      group.current.position.y = position[1] + Math.sin(t * 0.8) * 0.08;
    }
    if (screenRef.current) {
      screenRef.current.emissiveIntensity = 0.3 + Math.sin(state.clock.elapsedTime * 1.5) * 0.1;
    }
  });

  return (
    <group ref={group} position={position} scale={scale}>
      {/* Frame with rounded edges */}
      <RoundedBox args={[3.4, 2.4, 0.06]} radius={0.12} smoothness={4} position={[0, 0, -0.025]}>
        <meshStandardMaterial color="#1a1a1a" metalness={0.8} roughness={0.15} />
      </RoundedBox>
      {/* Screen bezel */}
      <RoundedBox args={[3.15, 2.05, 0.02]} radius={0.08} smoothness={4} position={[0, -0.05, 0.005]}>
        <meshStandardMaterial color="#111" metalness={0.3} roughness={0.5} />
      </RoundedBox>
      {/* Screen — emissive glow */}
      <RoundedBox args={[3.0, 1.9, 0.01]} radius={0.06} smoothness={4} position={[0, -0.05, 0.02]}>
        <meshStandardMaterial
          ref={screenRef}
          color="#0f1923"
          emissive="#1a2a44"
          emissiveIntensity={0.3}
          metalness={0.1}
          roughness={0.9}
        />
      </RoundedBox>
      {/* Top bar */}
      <mesh position={[0, 0.88, 0.03]}>
        <boxGeometry args={[2.95, 0.16, 0.008]} />
        <meshStandardMaterial color="#252535" emissive="#252535" emissiveIntensity={0.1} />
      </mesh>
      {/* Traffic light dots */}
      {[['#ff5f57', -1.25], ['#febc2e', -1.13], ['#28c840', -1.01]].map(([col, x]) => (
        <mesh key={col} position={[x, 0.88, 0.04]}>
          <sphereGeometry args={[0.035, 16, 16]} />
          <meshStandardMaterial color={col} emissive={col} emissiveIntensity={0.8} />
        </mesh>
      ))}
      {/* URL bar */}
      <mesh position={[0.15, 0.88, 0.04]}>
        <boxGeometry args={[1.5, 0.08, 0.006]} />
        <meshStandardMaterial color="#3a3a4a" emissive="#3a3a4a" emissiveIntensity={0.05} />
      </mesh>
      {/* ── Page content ── */}
      {/* Nav bar */}
      <mesh position={[0, 0.68, 0.03]}>
        <boxGeometry args={[2.8, 0.12, 0.006]} />
        <meshStandardMaterial color="#FED136" emissive="#FED136" emissiveIntensity={0.4} />
      </mesh>
      {/* Hero banner */}
      <mesh position={[0, 0.35, 0.03]}>
        <boxGeometry args={[2.8, 0.45, 0.006]} />
        <meshStandardMaterial color="#1e3a5f" emissive="#2563eb" emissiveIntensity={0.15} />
      </mesh>
      {/* Hero text placeholder */}
      <mesh position={[0, 0.38, 0.04]}>
        <boxGeometry args={[1.4, 0.06, 0.004]} />
        <meshStandardMaterial color="#ffffff" emissive="#ffffff" emissiveIntensity={0.3} transparent opacity={0.7} />
      </mesh>
      <mesh position={[0, 0.3, 0.04]}>
        <boxGeometry args={[0.9, 0.04, 0.004]} />
        <meshStandardMaterial color="#ffffff" emissive="#ffffff" emissiveIntensity={0.2} transparent opacity={0.4} />
      </mesh>
      {/* CTA button on hero */}
      <mesh position={[0, 0.2, 0.04]}>
        <boxGeometry args={[0.5, 0.08, 0.004]} />
        <meshStandardMaterial color="#FED136" emissive="#FED136" emissiveIntensity={0.5} />
      </mesh>
      {/* Content section */}
      <mesh position={[-0.7, -0.05, 0.03]}>
        <boxGeometry args={[1.2, 0.06, 0.004]} />
        <meshStandardMaterial color="#4a5568" emissive="#4a5568" emissiveIntensity={0.1} />
      </mesh>
      <mesh position={[0.7, -0.05, 0.03]}>
        <boxGeometry args={[1.0, 0.06, 0.004]} />
        <meshStandardMaterial color="#4a5568" emissive="#4a5568" emissiveIntensity={0.1} />
      </mesh>
      {/* Cards row */}
      {[-0.95, 0, 0.95].map((x, i) => (
        <group key={i}>
          <mesh position={[x, -0.4, 0.03]}>
            <boxGeometry args={[0.82, 0.5, 0.006]} />
            <meshStandardMaterial
              color="#1e3a5f"
              emissive="#2563eb"
              emissiveIntensity={0.12 + i * 0.03}
            />
          </mesh>
          {/* Card accent line */}
          <mesh position={[x, -0.18, 0.04]}>
            <boxGeometry args={[0.7, 0.03, 0.004]} />
            <meshStandardMaterial color="#FED136" emissive="#FED136" emissiveIntensity={0.3} />
          </mesh>
        </group>
      ))}
      {/* Monitor stand */}
      <RoundedBox args={[0.4, 0.15, 0.04]} radius={0.02} smoothness={4} position={[0, -1.15, -0.05]}>
        <meshStandardMaterial color="#1a1a1a" metalness={0.8} roughness={0.2} />
      </RoundedBox>
      <RoundedBox args={[1.0, 0.06, 0.3]} radius={0.03} smoothness={4} position={[0, -1.25, -0.05]}>
        <meshStandardMaterial color="#1a1a1a" metalness={0.8} roughness={0.2} />
      </RoundedBox>
    </group>
  );
}

/* ── Tablet ── */
function TabletDevice({ position = [0, 0, 0], scale = 1 }) {
  const group = useRef();
  const screenRef = useRef();

  useFrame((state) => {
    if (group.current) {
      const t = state.clock.elapsedTime;
      group.current.rotation.y = Math.sin(t * 0.4 + 1.5) * 0.1 + 0.1;
      group.current.rotation.x = Math.cos(t * 0.25 + 1) * 0.04 - 0.03;
      group.current.position.y = position[1] + Math.sin(t * 0.7 + 1) * 0.1;
    }
    if (screenRef.current) {
      screenRef.current.emissiveIntensity = 0.25 + Math.sin(state.clock.elapsedTime * 1.2 + 1) * 0.08;
    }
  });

  return (
    <group ref={group} position={position} scale={scale}>
      {/* Body with rounded corners */}
      <RoundedBox args={[1.7, 2.45, 0.07]} radius={0.15} smoothness={4} position={[0, 0, -0.02]}>
        <meshStandardMaterial color="#1a1a1a" metalness={0.7} roughness={0.2} />
      </RoundedBox>
      {/* Screen bezel */}
      <RoundedBox args={[1.52, 2.2, 0.01]} radius={0.1} smoothness={4} position={[0, 0.03, 0.015]}>
        <meshStandardMaterial color="#111" />
      </RoundedBox>
      {/* Screen */}
      <RoundedBox args={[1.45, 2.1, 0.008]} radius={0.08} smoothness={4} position={[0, 0.03, 0.025]}>
        <meshStandardMaterial
          ref={screenRef}
          color="#0f1923"
          emissive="#1a2a44"
          emissiveIntensity={0.25}
          metalness={0.05}
          roughness={0.95}
        />
      </RoundedBox>
      {/* Status bar */}
      <mesh position={[0, 1.0, 0.035]}>
        <boxGeometry args={[1.35, 0.05, 0.004]} />
        <meshStandardMaterial color="#333" emissive="#333" emissiveIntensity={0.1} />
      </mesh>
      {/* Header */}
      <mesh position={[0, 0.88, 0.035]}>
        <boxGeometry args={[1.35, 0.1, 0.004]} />
        <meshStandardMaterial color="#FED136" emissive="#FED136" emissiveIntensity={0.4} />
      </mesh>
      {/* Hero */}
      <mesh position={[0, 0.6, 0.035]}>
        <boxGeometry args={[1.35, 0.35, 0.004]} />
        <meshStandardMaterial color="#1e3a5f" emissive="#2563eb" emissiveIntensity={0.12} />
      </mesh>
      {/* Hero text */}
      <mesh position={[0, 0.63, 0.04]}>
        <boxGeometry args={[0.8, 0.04, 0.003]} />
        <meshStandardMaterial color="#fff" emissive="#fff" emissiveIntensity={0.2} transparent opacity={0.6} />
      </mesh>
      {/* Text lines */}
      <mesh position={[0, 0.3, 0.035]}>
        <boxGeometry args={[1.15, 0.04, 0.004]} />
        <meshStandardMaterial color="#4a5568" emissive="#4a5568" emissiveIntensity={0.1} />
      </mesh>
      <mesh position={[0, 0.22, 0.035]}>
        <boxGeometry args={[0.9, 0.04, 0.004]} />
        <meshStandardMaterial color="#4a5568" emissive="#4a5568" emissiveIntensity={0.1} />
      </mesh>
      {/* Cards — 2 columns */}
      {[[-0.37, 0.01], [0.37, 0.01]].map(([x, y], i) => (
        <group key={i}>
          <mesh position={[x, y, 0.035]}>
            <boxGeometry args={[0.62, 0.32, 0.004]} />
            <meshStandardMaterial color="#1e3a5f" emissive="#2563eb" emissiveIntensity={0.1} />
          </mesh>
          <mesh position={[x, y + 0.12, 0.04]}>
            <boxGeometry args={[0.5, 0.02, 0.003]} />
            <meshStandardMaterial color="#FED136" emissive="#FED136" emissiveIntensity={0.3} />
          </mesh>
        </group>
      ))}
      {/* Bottom nav */}
      <mesh position={[0, -0.55, 0.035]}>
        <boxGeometry args={[1.35, 0.07, 0.004]} />
        <meshStandardMaterial color="#252535" emissive="#252535" emissiveIntensity={0.1} />
      </mesh>
      {/* Home indicator */}
      <mesh position={[0, -1.05, 0.035]}>
        <boxGeometry args={[0.3, 0.025, 0.004]} />
        <meshStandardMaterial color="#444" emissive="#444" emissiveIntensity={0.1} />
      </mesh>
    </group>
  );
}

/* ── Mobile phone ── */
function MobileDevice({ position = [0, 0, 0], scale = 1 }) {
  const group = useRef();
  const screenRef = useRef();

  useFrame((state) => {
    if (group.current) {
      const t = state.clock.elapsedTime;
      group.current.rotation.y = Math.sin(t * 0.55 + 3) * 0.12 + 0.08;
      group.current.rotation.x = Math.cos(t * 0.35 + 2) * 0.04 - 0.02;
      group.current.position.y = position[1] + Math.sin(t * 0.9 + 2) * 0.12;
    }
    if (screenRef.current) {
      screenRef.current.emissiveIntensity = 0.3 + Math.sin(state.clock.elapsedTime * 1.8 + 2) * 0.1;
    }
  });

  return (
    <group ref={group} position={position} scale={scale}>
      {/* Phone body — rounded like modern smartphone */}
      <RoundedBox args={[1.05, 2.1, 0.07]} radius={0.15} smoothness={4} position={[0, 0, -0.015]}>
        <meshStandardMaterial color="#1a1a1a" metalness={0.7} roughness={0.2} />
      </RoundedBox>
      {/* Bezel */}
      <RoundedBox args={[0.93, 1.88, 0.01]} radius={0.12} smoothness={4} position={[0, 0.01, 0.015]}>
        <meshStandardMaterial color="#111" />
      </RoundedBox>
      {/* Screen */}
      <RoundedBox args={[0.88, 1.82, 0.008]} radius={0.1} smoothness={4} position={[0, 0.01, 0.025]}>
        <meshStandardMaterial
          ref={screenRef}
          color="#0f1923"
          emissive="#1a2a44"
          emissiveIntensity={0.3}
          metalness={0.05}
          roughness={0.95}
        />
      </RoundedBox>
      {/* Dynamic Island */}
      <RoundedBox args={[0.25, 0.05, 0.004]} radius={0.025} smoothness={4} position={[0, 0.85, 0.035]}>
        <meshStandardMaterial color="#000" />
      </RoundedBox>
      {/* Status bar */}
      <mesh position={[0, 0.78, 0.035]}>
        <boxGeometry args={[0.8, 0.03, 0.004]} />
        <meshStandardMaterial color="#333" emissive="#333" emissiveIntensity={0.1} />
      </mesh>
      {/* Header */}
      <mesh position={[0, 0.68, 0.035]}>
        <boxGeometry args={[0.8, 0.08, 0.004]} />
        <meshStandardMaterial color="#FED136" emissive="#FED136" emissiveIntensity={0.4} />
      </mesh>
      {/* Hero */}
      <mesh position={[0, 0.45, 0.035]}>
        <boxGeometry args={[0.8, 0.3, 0.004]} />
        <meshStandardMaterial color="#1e3a5f" emissive="#2563eb" emissiveIntensity={0.15} />
      </mesh>
      {/* Hero text */}
      <mesh position={[0, 0.48, 0.04]}>
        <boxGeometry args={[0.5, 0.03, 0.003]} />
        <meshStandardMaterial color="#fff" emissive="#fff" emissiveIntensity={0.2} transparent opacity={0.5} />
      </mesh>
      {/* Text */}
      <mesh position={[0, 0.22, 0.035]}>
        <boxGeometry args={[0.7, 0.03, 0.004]} />
        <meshStandardMaterial color="#4a5568" emissive="#4a5568" emissiveIntensity={0.1} />
      </mesh>
      <mesh position={[0, 0.15, 0.035]}>
        <boxGeometry args={[0.55, 0.03, 0.004]} />
        <meshStandardMaterial color="#4a5568" emissive="#4a5568" emissiveIntensity={0.1} />
      </mesh>
      {/* Cards stacked */}
      {[0.0, -0.28].map((y, i) => (
        <group key={i}>
          <mesh position={[0, y, 0.035]}>
            <boxGeometry args={[0.75, 0.2, 0.004]} />
            <meshStandardMaterial color="#1e3a5f" emissive="#2563eb" emissiveIntensity={0.1} />
          </mesh>
          <mesh position={[0, y + 0.065, 0.04]}>
            <boxGeometry args={[0.6, 0.015, 0.003]} />
            <meshStandardMaterial color="#FED136" emissive="#FED136" emissiveIntensity={0.3} />
          </mesh>
        </group>
      ))}
      {/* Bottom bar */}
      <mesh position={[0, -0.62, 0.035]}>
        <boxGeometry args={[0.8, 0.06, 0.004]} />
        <meshStandardMaterial color="#252535" emissive="#252535" emissiveIntensity={0.1} />
      </mesh>
      {/* Home indicator */}
      <mesh position={[0, -0.88, 0.035]}>
        <boxGeometry args={[0.22, 0.02, 0.004]} />
        <meshStandardMaterial color="#555" emissive="#555" emissiveIntensity={0.15} />
      </mesh>
    </group>
  );
}


// The full 3D scene — arc/semicircle arrangement
function Scene() {
  const groupRef = useRef();
  const { size, camera } = useThree();
  const isMobile = size.width <= 768;
  const isSmallMobile = size.width <= 480;
  const isLandscape = size.width > size.height && size.height <= 500;

  // Adjust camera once when size changes, not every frame
  React.useEffect(() => {
    if (isLandscape) {
      camera.position.set(0, 0.5, 9);
      camera.fov = 42;
    } else if (isSmallMobile) {
      camera.position.set(0, -0.5, 8);
      camera.fov = 50;
    } else if (isMobile) {
      camera.position.set(0, -0.3, 8);
      camera.fov = 48;
    } else {
      camera.position.set(0, 1.8, 11);
      camera.fov = 32;
    }
    camera.updateProjectionMatrix();
  }, [isMobile, isSmallMobile, isLandscape, camera]);

  useFrame((state) => {
    if (groupRef.current) {
      groupRef.current.rotation.y = Math.sin(state.clock.elapsedTime * 0.12) * 0.06;
    }
  });

  // Semicircle arc: desktop center, tablet left, mobile right
  // On mobile: tight layout, devices side by side instead of arc
  const arcRadius = isLandscape ? 4 : isMobile ? 1.2 : 3;
  const desktopAngle = 0;       // center
  const tabletAngle = isLandscape ? -0.5 : isMobile ? -0.9 : -0.65;    // left
  const mobileAngle = isLandscape ? 0.5 : isMobile ? 0.9 : 0.65;      // right

  return (
    <>
      {/* Rich lighting setup */}
      <ambientLight intensity={0.3} />
      <directionalLight position={[5, 8, 5]} intensity={0.6} color="#ffffff" />
      <pointLight position={[-5, 3, 4]} intensity={0.5} color="#FED136" distance={14} />
      <pointLight position={[5, -2, 4]} intensity={0.3} color="#2563eb" distance={12} />
      <pointLight position={[0, 0, 6]} intensity={0.25} color="#ffffff" distance={10} />
      <spotLight
        position={[0, 6, 8]}
        angle={0.5}
        penumbra={0.8}
        intensity={0.5}
        color="#ffffff"
      />

      <group ref={groupRef} position={[0, isLandscape ? -3 : isMobile ? -4.5 : -1.6, 0]}>
        {/* Desktop — center of the arc, slightly back */}
        <DesktopBrowser
          position={[
            Math.sin(desktopAngle) * arcRadius,
            0.3,
            -Math.cos(desktopAngle) * arcRadius + arcRadius - (isMobile || isLandscape ? 0 : 1)
          ]}
          scale={isLandscape ? 0.4 : isMobile ? 0.4 : 0.45}
        />

        {/* Tablet — left side of arc */}
        <TabletDevice
          position={[
            Math.sin(tabletAngle) * arcRadius + (isMobile || isLandscape ? 0 : -0.3),
            isMobile || isLandscape ? 0 : -0.2,
            -Math.cos(tabletAngle) * arcRadius + arcRadius + (isMobile || isLandscape ? 0 : 0.5)
          ]}
          scale={isLandscape ? 0.28 : isMobile ? 0.28 : 0.32}
        />

        {/* Mobile — right side of arc */}
        <MobileDevice
          position={[
            Math.sin(mobileAngle) * arcRadius + (isMobile || isLandscape ? 0 : 0.3),
            isMobile || isLandscape ? 0 : -0.2,
            -Math.cos(mobileAngle) * arcRadius + arcRadius + (isMobile || isLandscape ? 0 : 0.5)
          ]}
          scale={isLandscape ? 0.22 : isMobile ? 0.22 : 0.26}
        />

        {/* Ripple effects under devices */}
        {isMobile ? (
          <>
            <RippleEffect
              position={[Math.sin(desktopAngle) * arcRadius, -0.5, -Math.cos(desktopAngle) * arcRadius + arcRadius]}
              radiusX={0.8}
              color="#FED136"
            />
            <RippleEffect
              position={[Math.sin(tabletAngle) * arcRadius, -0.6, -Math.cos(tabletAngle) * arcRadius + arcRadius]}
              radiusX={0.5}
              color="#FED136"
            />
            <RippleEffect
              position={[Math.sin(mobileAngle) * arcRadius, -0.6, -Math.cos(mobileAngle) * arcRadius + arcRadius]}
              radiusX={0.4}
              color="#FED136"
            />
          </>
        ) : (
          <>
            <RippleEffect
              position={[Math.sin(desktopAngle) * arcRadius, -0.7, -Math.cos(desktopAngle) * arcRadius + arcRadius - 1]}
              radiusX={1.2}
              color="#FED136"
            />
            <RippleEffect
              position={[Math.sin(tabletAngle) * arcRadius - 0.3, -0.9, -Math.cos(tabletAngle) * arcRadius + arcRadius + 0.5]}
              radiusX={0.7}
              color="#FED136"
            />
            <RippleEffect
              position={[Math.sin(mobileAngle) * arcRadius + 0.3, -0.9, -Math.cos(mobileAngle) * arcRadius + arcRadius + 0.5]}
              radiusX={0.5}
              color="#FED136"
            />
          </>
        )}
      </group>

      <Particles count={100} />
      <Stars count={350} />
    </>
  );
}

/* ─── Main component ─── */

const HeroSection = () => {
  const [showScroll, setShowScroll] = React.useState(false);

  React.useEffect(() => {
    const check = () => {
      // Hide on small screens (mobile portrait) and landscape mobile
      const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
      const isLandscapeMobile = window.innerHeight <= 500;
      setShowScroll(!isTouchDevice && !isLandscapeMobile && window.innerWidth > 768);
    };
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  const scrollToSection = (sectionId) => {
    const section = document.getElementById(sectionId);
    if (section) section.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <HeroContainer id="accueil">
      {/* Full-screen 3D canvas as background */}
      <DevicesCanvas>
        <Canvas
          camera={{ position: [0, 1.8, 9], fov: 35 }}
          dpr={[1, 2]}
          gl={{ antialias: true, alpha: true, toneMapping: THREE.ACESFilmicToneMapping, toneMappingExposure: 1.2 }}
          style={{ background: 'transparent' }}
        >
          <Suspense fallback={null}>
            <Scene />
          </Suspense>
        </Canvas>
      </DevicesCanvas>

      {/* Centered text and buttons overlay */}
      <HeroContent>
        <HeroTitle
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          <span className="highlight">Sites web</span> & <span className="highlight">applications</span><br />
          sur mesure
        </HeroTitle>

        <HeroSubtitle
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
        >
          Conçus pour faire évoluer votre activité.
        </HeroSubtitle>

        <ButtonContainer
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.6 }}
          style={{ pointerEvents: 'auto' }}
        >
          <PrimaryButton onClick={() => scrollToSection('devis')}>Estimez votre projet</PrimaryButton>
          <SecondaryButton onClick={() => scrollToSection('contact')}>Me contacter</SecondaryButton>
        </ButtonContainer>
      </HeroContent>

      {showScroll && (
        <ScrollIndicator
          initial={{ opacity: 0 }}
          animate={{ opacity: 0.7 }}
          transition={{ duration: 1, delay: 1.5 }}
        >
          <div className="mouse">
            <div className="wheel"></div>
          </div>
          <span>SCROLL</span>
        </ScrollIndicator>
      )}
    </HeroContainer>
  );
};

export default HeroSection;
